import { motion } from "framer-motion";
import { Star, Quotes, ArrowRight } from "@phosphor-icons/react";
import Newsletter from "@/components/Newsletter";
import { TESTIMONIALS, waLink } from "@/lib/site";

const EXTENDED_TESTIMONIALS = [
    ...TESTIMONIALS,
    {
        name: "Ravi & Sunita Kapoor",
        role: "Anniversary — Santorini",
        rating: 5,
        text: "Our 25th anniversary trip was everything we dreamed of. The cave suite, the private yacht at sunset — Midasis thought of details we wouldn't have even known to ask for.",
        image: "https://images.unsplash.com/photo-1623594675959-02360202d4d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMHNtaWxpbmd8ZW58MHx8fHwxNzc2MzQ4MjY0fDA&ixlib=rb-4.1.0&q=85",
    },
    {
        name: "Ananya Krishnan",
        role: "Family — Kenya Safari",
        rating: 5,
        text: "Traveling with three kids under 10 felt impossible until Midasis stepped in. They found a camp with a kids' ranger program, and my youngest still talks about the baby elephant.",
        image: "https://images.unsplash.com/photo-1750187655549-b5009f762677?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMHNtaWxpbmd8ZW58MHx8fHwxNzc2MzQ4MjY0fDA&ixlib=rb-4.1.0&q=85",
    },
    {
        name: "Vikram Nair",
        role: "Business + Leisure — Dubai",
        rating: 5,
        text: "I had three days between meetings in Dubai. Midasis turned it into a proper mini-break — desert camp, Burj dinner, and a morning at the spa. Seamless.",
        image: "https://images.pexels.com/photos/17582358/pexels-photo-17582358.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
];

export default function TestimonialsPage() {
    return (
        <div data-testid="page-testimonials" className="pt-28">
            {/* Hero */}
            <section className="py-20 bg-navy">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                    <p className="overline">Stories</p>
                    <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.05] mt-6 tracking-tight max-w-4xl">
                        Letters from <em className="italic text-gold">the road.</em>
                    </h1>
                    <p className="font-sans text-base text-white/65 mt-6 max-w-2xl leading-relaxed">
                        We have the privilege of sending thousands of travelers home with stories. Here are a few we are
                        particularly fond of.
                    </p>
                </div>
            </section>

            {/* Featured testimonial */}
            <section className="py-24 lg:py-32 bg-navy" data-testid="testimonials-featured">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="relative border border-white/10 p-10 lg:p-16 bg-[#0F1730]"
                    >
                        <Quotes size={64} weight="fill" className="text-gold/10 absolute top-8 right-8" />
                        <div className="flex items-center gap-1 text-gold mb-6">
                            {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={18} weight="fill" />)}
                        </div>
                        <blockquote className="font-serif italic text-2xl sm:text-3xl lg:text-4xl leading-snug text-white max-w-4xl">
                            "{TESTIMONIALS[0].text}"
                        </blockquote>
                        <figcaption className="mt-10 flex items-center gap-5 pt-8 border-t border-white/10">
                            <img src={TESTIMONIALS[0].image} alt={TESTIMONIALS[0].name} className="w-14 h-14 object-cover rounded-full border border-gold/40" />
                            <div>
                                <p className="font-sans text-base text-white">{TESTIMONIALS[0].name}</p>
                                <p className="font-sans text-xs text-white/50 uppercase tracking-widest mt-0.5">{TESTIMONIALS[0].role}</p>
                            </div>
                        </figcaption>
                    </motion.div>
                </div>
            </section>

            {/* All testimonials grid */}
            <section className="pb-24 lg:pb-32 bg-navy" data-testid="testimonials-grid">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                        {EXTENDED_TESTIMONIALS.slice(1).map((t, i) => (
                            <motion.figure
                                key={t.name + i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.7, delay: i * 0.08 }}
                                className="relative p-8 lg:p-10 bg-navy-surface border border-white/10 flex flex-col h-full"
                                data-testid={`testimonial-card-${i}`}
                            >
                                <Quotes size={32} weight="fill" className="text-gold/60 absolute top-6 right-6" />
                                <div className="flex items-center gap-1 text-gold">
                                    {Array.from({ length: t.rating }).map((_, idx) => (
                                        <Star key={idx} size={14} weight="fill" />
                                    ))}
                                </div>
                                <blockquote className="font-serif italic text-lg lg:text-xl leading-snug text-white mt-5">
                                    "{t.text}"
                                </blockquote>
                                <figcaption className="mt-8 flex items-center gap-4 pt-6 border-t border-white/10">
                                    <img src={t.image} alt={t.name} className="w-12 h-12 object-cover rounded-full border border-gold/40" />
                                    <div>
                                        <p className="font-sans text-sm text-white">{t.name}</p>
                                        <p className="font-sans text-xs text-white/50 uppercase tracking-widest mt-0.5">{t.role}</p>
                                    </div>
                                </figcaption>
                            </motion.figure>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 bg-[#080E20] border-t border-white/5" data-testid="testimonials-cta">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 text-center">
                    <p className="overline">Your story next?</p>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium mt-4 leading-tight max-w-2xl mx-auto">
                        Let's give you something <em className="italic text-gold">worth writing home about.</em>
                    </h2>
                    <div className="mt-10 flex flex-wrap justify-center gap-4">
                        <a
                            href={waLink("Hi! I'd like to start planning my trip with Midasis.")}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 bg-gold text-navy hover:bg-[#E5C158] px-8 py-4 font-sans font-medium uppercase tracking-wider text-xs transition-colors"
                        >
                            Start planning <ArrowRight size={14} weight="bold" />
                        </a>
                        <a
                            href="/packages"
                            className="inline-flex items-center gap-2 border border-white/25 hover:border-gold hover:text-gold px-8 py-4 font-sans font-medium uppercase tracking-wider text-xs transition-colors"
                        >
                            Browse packages
                        </a>
                    </div>
                </div>
            </section>

            <Newsletter />
        </div>
    );
}
